import searchAtomPair, { SearchParams } from 'atoms/search-atom';
import { mutationAtomWithLoadable } from 'lib/atoms';
import request from 'lib/request';
import { apiConfig } from 'types/api-config';
import type { AfeCatalogFacets } from 'types/api-responses';
import type { AfeCatalogResult, AfeCatalogSummary } from './types';
import type { PartialAfeSaveRequest } from './new/types';

/** Source filter values — auto-connected AFEs vs manually created. */
export type AfeSourceFilter = 'auto_connected' | 'manual';

/** Search parameters for the AFE Catalog query, extending base search params with filter fields. */
export interface AfeCatalogParams extends SearchParams {
  basinNames?: string[];
  states?: string[];
  counties?: string[];
  operators?: string[];
  reportedFormations?: string[];
  afeTypes?: string[];
  wellTypes?: string[];
  source?: AfeSourceFilter;
  dateFilter?: {
    type: string;
    start?: string;
    end?: string;
  };
}

/**
 * Creates a search atom pair for the AFE Catalog query.
 * No defaultPayload — the useEffect in AfeCatalogContent triggers all fetches,
 * including the initial one with DEFAULT_PARAMS from utils.
 * @return {{ searchAtom: Atom, resetSearchDataAtom: Atom }} Object with searchAtom and resetSearchDataAtom.
 */
export const createAfeCatalogSearchAtom = () => {
  return searchAtomPair<
    AfeCatalogResult,
    AfeCatalogParams,
    AfeCatalogFacets,
    AfeCatalogSummary
  >({ href: '/queries/afe_catalog' });
};

/**
 * Soft-deletes an AFE record via DELETE /afe_headers/:id. BE returns
 * 204 No Content on success, 404 if the id doesn't exist or is already
 * soft-deleted, and 422 reserved for a future before_destroy throw :abort
 * guard. Any non-success response throws so the loadable's hasError state
 * drives the consumer's failure branch.
 * @return {[Atom, Atom<Loadable<void>>]} Base and loadable atoms.
 * @throws {Error} If the delete request returns a non-success response.
 */
export const [deleteAfeAtom, deleteAfeLoadableAtom] = mutationAtomWithLoadable<
  void,
  [number]
>(async (_get, _set, id: number) => {
  const response = await request.deleteReq<unknown, unknown>(
    `/afe_headers/${id}`,
    null,
    new AbortController()
  );
  if (response.type !== 'success') {
    throw new Error('Failed to delete AFE');
  }
});

/**
 * Hard-deletes a single supporting file from an AFE via PATCH
 * /afe_headers/:afeId. The payload is diff-only — its `afe_header` body
 * contains nothing except the `_destroy` entry under
 * `supporting_files_attributes`. Used by the detail-page's per-row
 * Delete (Story 2 Flow 4). The wizard's edit-mode delete-existing-file
 * flow does NOT use this atom; those deletions ride inline on the wizard
 * save PATCH via the wizard's own atoms.
 *
 * Failure modes mapped to the same rejection ("Failed to delete supporting
 * file") so the loadable's `hasError` state drives a uniform consumer
 * branch — per-status copy is deferred to Open Question #3 in requirements.
 *
 * @param {number} afeId - ID of the AFE whose supporting file is being deleted.
 * @param {number} fileId - ID of the SupportingFile record to hard-delete.
 * @return {[Atom, Atom<Loadable<void>>]} Base and loadable atoms.
 * @throws {Error} If the PATCH returns a non-success response (422 invalid,
 *   404 cross-AFE leakage, or network error).
 */
export const [
  deleteAfeSupportingFileAtom,
  deleteAfeSupportingFileLoadableAtom,
] = mutationAtomWithLoadable<void, [number, number]>(
  async (_get, _set, afeId: number, fileId: number) => {
    const response = await request.patch<unknown, PartialAfeSaveRequest>(
      `/afe_headers/${afeId}`,
      {
        afe_header: {
          supporting_files_attributes: [{ id: fileId, _destroy: true }],
        },
      },
      new AbortController()
    );
    if (response.type !== 'success') {
      throw new Error('Failed to delete supporting file');
    }
  }
);

/**
 * Enqueues an async XLSX export of the entire filtered AFE catalog via
 * POST /afe_headers/export. Returns the `exportId` so the caller can
 * correlate the Pusher `afe_catalog_export_complete` event on
 * `user_<id>_exports`. Throws with the server-supplied error message on
 * failure so the loadable's `hasError` state (or a try/catch consumer)
 * drives the failure branch.
 *
 * @param {AfeCatalogParams} params - Current filters/sort. Extra keys
 *   (page, pageSize) are silently stripped by the controller's permit
 *   list, so the full search-atom params object can be passed verbatim.
 * @return {[Atom, Atom<Loadable<{ exportId: string }>>]} Base and loadable atoms.
 * @throws {Error} If the POST returns a non-success response.
 */
export const [exportAfeCatalogAtom, exportAfeCatalogLoadableAtom] =
  mutationAtomWithLoadable<{ exportId: string }, [AfeCatalogParams]>(
    async (_get, _set, params: AfeCatalogParams) => {
      const response = await request.post<
        { exportId: string; status: string; message?: string },
        { errors?: unknown; error?: string }
      >(apiConfig.AFE_CATALOG_EXPORT_URL, params, new AbortController());

      if (response.type !== 'success') {
        const message =
          response.type === 'error' && response.data?.error
            ? response.data.error
            : 'Failed to start export. Please try again.';
        throw new Error(message);
      }

      return { exportId: response.data.exportId };
    }
  );
